/**
 * Geometrie- und Textur-Hilfsfunktionen für den FogRenderer.
 * Baut Hex-Geometry, Hex-Indices, Layer-States und Noise-Textur.
 */
import type { FogOverlayConfig } from '../config/FogConfig';

export interface LayerState {
  baseY: number;
  noiseFreq: number;
  noiseAmp: number;
  cloudDetail: number;
  layerParallax: number;
  parallaxNormalY: number;
  colorR: number;
  colorG: number;
  colorB: number;
  alpha: number;
  time: number;
  timeScale: number;
}

export function buildLayerStates(ov: FogOverlayConfig): LayerState[] {
  const count = Math.max(1, ov.layers);
  const perAlpha = ov.alpha / count;
  const spacing = count > 1 ? ov.thickness / (count - 1) : 0;
  const startY = ov.height - ov.thickness * 0.5;
  const states: LayerState[] = [];

  for (let i = 0; i < count; i++) {
    const layerOff = i - (count - 1) * 0.5;
    const norm = count > 1 ? i / (count - 1) : 0.5;
    const parallax = ov.volumetric.layerParallax * (norm * 2.0 - 1.0);
    const ci = Math.min(ov.layerColors.length - 1, Math.max(0, (count - 1) - i));
    const c = ov.layerColors[ci] ?? ov.color;

    states.push({
      baseY: startY + i * spacing,
      noiseFreq: ov.noiseFreq * (1.0 + layerOff * 0.07),
      noiseAmp: ov.noiseAmp * (1.0 + layerOff * 0.08),
      cloudDetail: ov.cloudDetail * (1.0 + layerOff * 0.05),
      layerParallax: parallax,
      parallaxNormalY: ov.volumetric.normalY * (1.0 + layerOff * 0.03),
      colorR: c[0], colorG: c[1], colorB: c[2],
      alpha: perAlpha,
      time: 0,
      timeScale: 1.0 + i * 0.12,
    });
  }
  return states;
}

/**
 * Tileable 256x256 FBM noise texture — baked at init, sampled per frame.
 * Replaces expensive per-pixel FBM in the fragment shader.
 */
export function createNoiseTexture(gl: WebGL2RenderingContext, size: number): WebGLTexture {
  const data = new Uint8Array(size * size);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const px = x / size;
      const py = y / size;
      let p0 = px * 6.28;
      let p1 = py * 6.28;

      let value = 0;
      let amp = 0.5;
      const r00 = 0.8, r01 = 0.6, r10 = -0.6, r11 = 0.8;

      for (let oct = 0; oct < 5; oct++) {
        const n = Math.sin(p0 * 1.17 + 0.3) * Math.cos(p1 * 0.93 + 0.7)
                + Math.sin(p1 * 1.31 - 0.5) * Math.cos(p0 * 0.79 - 0.2) * 0.5;
        value += amp * n;
        const np0 = (r00 * p0 + r01 * p1) * 1.95;
        const np1 = (r10 * p0 + r11 * p1) * 1.95;
        p0 = np0;
        p1 = np1;
        amp *= 0.48;
      }

      const normalized = (value + 1.0) * 0.5;
      data[y * size + x] = Math.max(0, Math.min(255, Math.round(normalized * 255)));
    }
  }

  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, size, size, 0, gl.RED, gl.UNSIGNED_BYTE, data);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.generateMipmap(gl.TEXTURE_2D);
  gl.bindTexture(gl.TEXTURE_2D, null);

  return tex;
}

export function buildFogHexGeometry(): Float32Array {
  const verts = new Float32Array(14);
  verts[0] = 0; verts[1] = 0;
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    verts[(i + 1) * 2] = Math.cos(angle);
    verts[(i + 1) * 2 + 1] = Math.sin(angle);
  }
  return verts;
}

export function buildHexIndices(): Uint16Array {
  return new Uint16Array([
    0, 1, 2, 0, 2, 3, 0, 3, 4,
    0, 4, 5, 0, 5, 6, 0, 6, 1,
  ]);
}
